import type { UIMessage } from '@ai-sdk/react'
import type { FileUIPart } from 'ai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { logger } from 'v2/utils/logger'
import type { BuddyFlowAttachmentRef, BuddyFlowEnvelope } from './buddy-flow-client'
import {
  bootstrapBuddyFlow,
  openBuddyFlowTurn,
  sendBuddyFlowPresetTurn,
  uploadBuddyFlowAttachment,
} from './buddy-flow-client'
import { getBuddyFlowConfig } from './buddy-flow-config'
import type { BuddyFlowIdentity } from './buddy-flow-context'
import { buildBuddyFlowCustomData } from './buddy-flow-context'
import {
  applyBuddyFlowFrame,
  closeFlowStreamingParts,
  FLOW_SIGNALS,
  readFlowErrorMessage,
  readFlowFrames,
  readFlowSignalName,
} from './buddy-flow-frames'
import type { BuddyChatBody, BuddyResponseSummary, BuddyStatus } from './use-buddy-chat'
import { summarizeAssistant } from './use-buddy-chat'

// The runner requires contact_identifier to be an RFC-4122 uuid and allows ONE open
// conversation per contact (a second bootstrap for the same contact answers
// "bootstrap produced no chat state"). A Buddy "new conversation" must therefore be a
// new contact, so every bootstrap mints its own id; the org identity rides in
// custom_data regardless.
export const buddyFlowContactIdentifier = (): string => uuidv4()
import type { Part } from './utils'

interface UseBuddyFlowChatOptions {
  buildBody: () => BuddyChatBody
  buildIdentity: () => BuddyFlowIdentity
  /** Org that owns the session handle, so it is never carried across a logout or org switch. */
  getSessionOwner?: () => string
  onFinish?: (summary: BuddyResponseSummary) => void
  onError?: (error: Error) => void
}

// The runner's own handle for the conversation, kept in sessionStorage so a reload
// within the tab re-attaches instead of starting a second conversation. Scoped by
// org for the same reason the aiassist conversation id is: sessionStorage survives
// both logout and an org switch, and a leaked handle would hand the next tenant in
// the tab someone else's transcript.
const FLOW_SESSION_STORAGE_KEY = 'buddy_flow_session'

type FlowMode = BuddyFlowEnvelope['bootstrap_state']

interface StoredFlowSession {
  owner: string
  sessionId: string
  agentUrl: string
  mode: FlowMode
}

/**
 * Shown when the console cannot yet name the account. Every flow action carries the
 * org in `AOM_UUID` / `auth-id` headers built from custom_data, so bootstrapping
 * without them mints a conversation whose every tool call fails — aiassist answers
 * 400 and core resolves no account — and the customer only ever sees the tool error.
 */
export const IDENTITY_NOT_READY_MESSAGE =
  "Buddy can't verify your account yet. Give it a second and try again."

export const isIdentityNotReadyError = (error: Error): boolean =>
  error.message === IDENTITY_NOT_READY_MESSAGE

const HANDOFF_TEXT =
  "I've passed this to our support team. They'll pick it up from here and reply by email — start a new conversation if you need anything else in the meantime."
const BUSY_TEXT = "I'm still finishing the last reply. Give it a moment and send that again."
const ENDED_TEXT = 'This conversation has ended. Start a new one whenever you need.'

// One retry on a 409 is enough: the lock is held by the turn we just sent, and it
// clears as soon as that turn drains.
const TURN_RETRY_DELAY_MS = 1000

const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}-${Date.now()}`

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export const readStoredFlowSession = (owner: string): StoredFlowSession | null => {
  try {
    const raw = sessionStorage.getItem(FLOW_SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredFlowSession> | null
    if (!parsed || typeof parsed.sessionId !== 'string' || typeof parsed.owner !== 'string') {
      return null
    }
    // An empty owner means the profile has not settled yet; that cannot prove a
    // mismatch, so the handle is reused and rebinds once the real owner arrives.
    if (owner && parsed.owner !== owner) return null
    return {
      owner: parsed.owner,
      sessionId: parsed.sessionId,
      agentUrl: parsed.agentUrl ?? '',
      mode: (parsed.mode ?? 'ai') as FlowMode,
    }
  } catch {
    return null
  }
}

export const writeStoredFlowSession = (session: StoredFlowSession): void => {
  try {
    sessionStorage.setItem(FLOW_SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Private browsing or a full store — the handle still works for this turn, it
    // just will not survive a reload.
  }
}

export const clearStoredFlowSession = (): void => {
  try {
    sessionStorage.removeItem(FLOW_SESSION_STORAGE_KEY)
  } catch {
    // Same fail-soft as the write.
  }
}

const assistantMessage = (parts: Part[] = []): UIMessage => ({
  id: newId(),
  role: 'assistant',
  parts,
})

const textMessage = (text: string): UIMessage =>
  assistantMessage([{ type: 'text', text, state: 'done' }])

export const useBuddyFlowChat = (opts: UseBuddyFlowChatOptions) => {
  const [messages, setMessages] = useState<UIMessage[]>([])
  const [status, setStatus] = useState<BuddyStatus>('ready')
  const [error, setError] = useState<Error | null>(null)
  // True once the flow has handed the conversation to a human: the composer closes
  // until the user starts a new conversation.
  const [isHandedOff, setIsHandedOff] = useState(false)
  const messagesRef = useRef<UIMessage[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const sessionRef = useRef<StoredFlowSession | null>(null)
  const optsRef = useRef(opts)
  optsRef.current = opts

  const writeMessages = useCallback((updater: (prev: UIMessage[]) => UIMessage[]) => {
    const next = updater(messagesRef.current)
    messagesRef.current = next
    setMessages(next)
  }, [])

  const setMessagesPublic = useCallback(
    (updater: UIMessage[] | ((prev: UIMessage[]) => UIMessage[])) => {
      writeMessages((prev) =>
        typeof updater === 'function'
          ? (updater as (p: UIMessage[]) => UIMessage[])(prev)
          : updater,
      )
    },
    [writeMessages],
  )

  const owner = useCallback(() => optsRef.current.getSessionOwner?.() ?? '', [])

  const dropSession = useCallback(() => {
    sessionRef.current = null
    clearStoredFlowSession()
  }, [])

  const keepSession = useCallback(
    (envelope: BuddyFlowEnvelope) => {
      const session: StoredFlowSession = {
        owner: owner(),
        sessionId: envelope.session_id,
        agentUrl: envelope.agent_url ?? '',
        mode: envelope.bootstrap_state,
      }
      sessionRef.current = session
      writeStoredFlowSession(session)
    },
    [owner],
  )

  const resetConversation = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    dropSession()
    setIsHandedOff(false)
    setError(null)
    setStatus('ready')
    writeMessages(() => [])
  }, [dropSession, writeMessages])

  const clearError = useCallback(() => setError(null), [])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setStatus('ready')
  }, [])

  const fail = useCallback((err: Error) => {
    setError(err)
    setStatus('error')
    optsRef.current.onError?.(err)
  }, [])

  const say = useCallback(
    (text: string) => {
      writeMessages((prev) => [...prev, textMessage(text)])
    },
    [writeMessages],
  )

  const handOff = useCallback(
    (text?: string) => {
      setIsHandedOff(true)
      say(text || HANDOFF_TEXT)
      setStatus('ready')
    },
    [say],
  )

  /**
   * Read one turn's SSE stream into a fresh assistant message. Signal and error
   * frames ride the same stream, so they are read here rather than in a second pass.
   */
  const consumeTurnStream = useCallback(
    async (response: Response, signal: AbortSignal): Promise<void> => {
      if (!response.body) throw new Error('Turn response has no body')
      setStatus('streaming')
      const assistantId = newId()
      writeMessages((prev) => [...prev, { id: assistantId, role: 'assistant', parts: [] }])

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let streamError: Error | null = null
      let closed = false

      try {
        while (!signal.aborted) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const { frames, rest, done: sawDone } = readFlowFrames(buffer)
          buffer = rest
          for (const frame of frames) {
            if (frame.type === 'error') {
              streamError = new Error(readFlowErrorMessage(frame))
              continue
            }
            if (frame.type === 'signal') {
              const name = readFlowSignalName(frame)
              if (name === FLOW_SIGNALS.AGENT_ASSIGNING) {
                const waiting = frame.content?.message
                handOff(typeof waiting === 'string' ? waiting : undefined)
              } else if (name === FLOW_SIGNALS.CONVERSATION_CLOSED) {
                // The walk is over; the next message starts a fresh conversation.
                closed = true
              }
              continue
            }
            writeMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? applyBuddyFlowFrame(m, frame) : m)),
            )
          }
          if (sawDone) break
        }
      } finally {
        reader.releaseLock?.()
      }

      writeMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? closeFlowStreamingParts(m) : m)),
      )
      if (closed) dropSession()
      if (streamError) throw streamError

      setStatus('ready')
      const finished = messagesRef.current.find((m) => m.id === assistantId)
      if (!finished) return
      optsRef.current.onFinish?.({
        ...summarizeAssistant(finished),
        sessionId: sessionRef.current?.sessionId,
      })
    },
    [dropSession, handOff, writeMessages],
  )

  const uploadAttachments = useCallback(
    async (files: FileUIPart[]): Promise<BuddyFlowAttachmentRef[]> =>
      Promise.all(
        files.map((file) =>
          uploadBuddyFlowAttachment({
            url: file.url,
            filename: file.filename,
            mediaType: file.mediaType,
          }),
        ),
      ),
    [],
  )

  const renderPreMessages = useCallback(
    (envelope: BuddyFlowEnvelope) => {
      const pre = envelope.pre_ai_messages ?? []
      if (!pre.length) return
      const rendered = pre.reduce<UIMessage>(
        (msg, frame) => applyBuddyFlowFrame(msg, frame),
        assistantMessage(),
      )
      if (rendered.parts.length) writeMessages((prev) => [...prev, rendered])
    },
    [writeMessages],
  )

  const sendMessage = useCallback(
    async ({ text, files }: { text: string; files?: FileUIPart[] }) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setError(null)
      setStatus('submitted')

      const trimmedText = text || ''
      const parts: Part[] = []
      if (trimmedText) parts.push({ type: 'text', text: trimmedText })
      for (const file of files ?? []) {
        parts.push({
          type: 'file',
          mediaType: file.mediaType,
          filename: file.filename,
          url: file.url,
        })
      }
      writeMessages((prev) => [...prev, { id: newId(), role: 'user', parts }])

      const config = getBuddyFlowConfig()
      const messageId = newId()

      const runTurn = async (
        session: StoredFlowSession,
        payload: { message?: string; attachments?: BuddyFlowAttachmentRef[]; trigger?: boolean },
        retriedOnConflict = false,
      ): Promise<void> => {
        const response = await openBuddyFlowTurn(
          {
            agentUrl: session.agentUrl,
            sessionId: session.sessionId,
            messageId,
            signal: controller.signal,
            ...payload,
          },
          config,
        )

        if (response.ok) {
          await consumeTurnStream(response, controller.signal)
          return
        }

        if (response.status === 404) {
          // The runner no longer holds this conversation — open a new one and
          // resend, so the message the customer just typed is not lost. The dead
          // handle is dropped inside startConversation, past its identity guard, so
          // an unidentified account is left exactly where it was.
          await startConversation(payload.message, payload.attachments)
          return
        }
        if (response.status === 409 && !retriedOnConflict) {
          await delay(TURN_RETRY_DELAY_MS)
          await runTurn(session, payload, true)
          return
        }
        if (response.status === 409) {
          say(BUSY_TEXT)
          setStatus('ready')
          return
        }
        if (response.status === 410) {
          handOff()
          return
        }
        throw new Error(
          response.status === 429 ? '429 Rate limit exceeded' : `HTTP ${response.status}`,
        )
      }

      const applyEnvelope = async (
        envelope: BuddyFlowEnvelope,
        message?: string,
        attachments?: BuddyFlowAttachmentRef[],
      ): Promise<void> => {
        keepSession(envelope)
        renderPreMessages(envelope)

        if (envelope.bootstrap_state === 'ai' && envelope.agent_url) {
          // The walk parked at an AI node, so the message that rode the bootstrap
          // is replayed as turn one over the stream.
          const session = sessionRef.current
          if (session) await runTurn(session, { message, attachments })
          return
        }
        if (envelope.bootstrap_state === 'human') {
          handOff(envelope.waiting_message)
          return
        }
        if (envelope.bootstrap_state === 'terminal' || envelope.end_message) {
          say(envelope.end_message || ENDED_TEXT)
          dropSession()
          setStatus('ready')
          return
        }
        // preset_wait and the reserved `waiting` state: the walk consumed the
        // message and the next reply goes to the preset-turn endpoint.
        setStatus('ready')
      }

      async function startConversation(
        message?: string,
        attachments?: BuddyFlowAttachmentRef[],
      ): Promise<void> {
        const { userContext, pageUrl } = optsRef.current.buildBody()
        const identity = optsRef.current.buildIdentity()
        if (!identity.aomUuid || !identity.authId) {
          const missing = [identity.aomUuid ? '' : 'aom_uuid', identity.authId ? '' : 'auth_id']
            .filter(Boolean)
            .join(' and ')
          logger.warn(`Buddy chat flow identity not ready; missing ${missing}`)
          throw new Error(IDENTITY_NOT_READY_MESSAGE)
        }
        // Past the guard, so a conversation is genuinely about to be opened.
        dropSession()
        const envelope = await bootstrapBuddyFlow(
          {
            contactIdentifier: buddyFlowContactIdentifier(),
            customerEmail: userContext.email,
            customData: buildBuddyFlowCustomData(userContext, pageUrl, identity),
            message,
            attachments,
            messageId,
          },
          config,
        )
        await applyEnvelope(envelope, message, attachments)
      }

      try {
        const attachments = files?.length ? await uploadAttachments(files) : undefined
        const session = sessionRef.current ?? readStoredFlowSession(owner())
        sessionRef.current = session

        if (!session) {
          await startConversation(trimmedText, attachments)
          return
        }
        if (session.mode === 'preset_wait') {
          const envelope = await sendBuddyFlowPresetTurn(
            session.sessionId,
            { message: trimmedText, attachments, messageId },
            config,
          )
          // A preset answer can hand straight back to the AI; the runner already
          // holds the words, so the follow-up turn carries no message.
          await applyEnvelope(envelope)
          const next = sessionRef.current
          if (envelope.bootstrap_state === 'ai' && next) {
            await runTurn(next, { trigger: true })
          }
          return
        }
        await runTurn(session, { message: trimmedText, attachments })
      } catch (e) {
        if (controller.signal.aborted) {
          setStatus('ready')
          return
        }
        const err = e instanceof Error ? e : new Error('Buddy chat flow failed')
        // The identity guard already logged which field was missing.
        if (!isIdentityNotReadyError(err)) logger.error('Buddy chat flow turn failed', err)
        fail(err)
      } finally {
        if (abortRef.current === controller) abortRef.current = null
      }
    },
    [
      consumeTurnStream,
      dropSession,
      fail,
      handOff,
      keepSession,
      owner,
      renderPreMessages,
      say,
      uploadAttachments,
      writeMessages,
    ],
  )

  useEffect(
    () => () => {
      abortRef.current?.abort()
    },
    [],
  )

  return {
    messages,
    sendMessage,
    setMessages: setMessagesPublic,
    resetConversation,
    status,
    error,
    clearError,
    stop,
    isHandedOff,
  }
}
